import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { NhlApiBase } from "../../lib/nhlApiBase";
import { stColors } from "./NhlSpecialTeams";

const GREY = "#8A94A6";
const rec = (r) => (r ? `${r.w}-${r.otw}-${r.otl}-${r.l}` : "0-0-0-0");
const fmtDate = (d) => { try { return new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return d; } };

// Season series between two NHL teams (shared by Game Centre + Playoff Matchups).
export const useSeasonSeries = (a, b, enabled = true) => {
    const NB = useContext(NhlApiBase);
    return useQuery({
        queryKey: ["nhl-season-series", NB, a, b],
        queryFn: () => api.get(`${NB}/season-series`, { params: { a, b } }).then((r) => r.data),
        enabled: !!a && !!b && enabled, staleTime: 5 * 60 * 1000,
    });
};


const tint = (c) => ({ background: `linear-gradient(180deg, ${c}55, ${c}22)`, borderColor: `${c}AA` });

// Season series, one row: Team 1 (logo + record) | each meeting (winner's colours) | Team 2 (logo + record).
export function NhlMatchup({ gameId, away, home, logoBy = {}, colorBy: rawColors = {} }) {
    const colorBy = stColors(away, home, rawColors);
    const NB = useContext(NhlApiBase);
    const { data: m } = useQuery({
        queryKey: ["nhl-matchup", NB, gameId, away, home],
        queryFn: () => api.get(`${NB}/matchup`, { params: { game: gameId, away, home } }).then((r) => r.data),
        enabled: !!away && !!home, staleTime: 5 * 60 * 1000,
    });
    if (!m) return null;
    // Live record: a game in progress counts for whoever is leading right now.
    const lg = (m.games || []).find((g) => g.live && g.away_score !== g.home_score);
    const recOf = (t) => {
        const r = { w: 0, otw: 0, otl: 0, l: 0, ...(m.record[t] || {}) };
        if (lg && (lg.away === t || lg.home === t)) {
            const lead = lg.away_score > lg.home_score ? lg.away : lg.home;
            if (lead === t) r.w += 1; else r.l += 1;
        }
        return r;
    };
    const team = (t) => (
        <div className="rounded-lg border flex flex-col items-center justify-center px-2 py-1 shrink-0 w-[64px] sm:w-[84px]" style={tint(colorBy[t] || GREY)} data-testid={`matchup-record-${t}`}>
            {logoBy[t] ? <img src={logoBy[t]} alt={t} className="h-6 w-6 object-contain" /> : <b className="text-[11px] text-white">{t}</b>}
            <span className="text-[10px] font-black text-white tabular-nums leading-tight">{rec(recOf(t))}</span>
            {lg && <span className="text-[7px] font-black uppercase tracking-widest text-white/70 leading-none">Live</span>}
        </div>
    );
    const games = (m.games || []).slice(0, 4);
    return (
        <div className="flex items-stretch gap-1.5 min-w-0" data-testid="nhl-gc-matchup">
            {team(away)}
            <div className="flex-1 min-w-0 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.max(games.length, 1)}, minmax(0,1fr))` }} data-testid="matchup-games">
                {games.map((g) => {
                    const cur = String(g.id) === String(gameId);
                    const done = g.final || g.live;
                    const win = g.final && g.away_score !== g.home_score ? (g.away_score > g.home_score ? g.away : g.home) : null;
                    const tag = g.live ? "Live" : g.final ? (g.last_period === "OT" ? "Final/OT" : g.last_period === "SO" ? "Final/SO" : "Final") : "Upcoming";
                    return (
                        <div key={g.id} className={`rounded-lg border overflow-hidden min-w-0 ${win ? "" : "bg-white/[0.03] border-white/10"} ${cur ? "ring-1 ring-white/60" : ""}`} style={win ? tint(colorBy[win] || GREY) : undefined} data-testid={`matchup-game-${g.id}`}>
                            <div className="flex items-center divide-x divide-white/15 bg-white/10 py-0.5 text-[9px] font-black uppercase tracking-wider text-white/70">
                                <span className="px-1.5 truncate">{fmtDate(g.date)}</span>
                                <span className={`px-1.5 ml-auto shrink-0 ${g.live ? "text-white" : ""}`}>{g.live && <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#DC2626] mr-1 align-middle animate-pulse" />}{tag}</span>
                            </div>
                            <div className="flex items-center justify-center gap-1.5 px-1.5 py-1">
                                {logoBy[g.away] ? <img src={logoBy[g.away]} alt={g.away} className="h-5 w-5 object-contain" /> : <b className="text-[10px] text-white">{g.away}</b>}
                                <b className="text-[13px] font-black text-white tabular-nums">{done ? `${g.away_score}-${g.home_score}` : "vs"}</b>
                                {logoBy[g.home] ? <img src={logoBy[g.home]} alt={g.home} className="h-5 w-5 object-contain" /> : <b className="text-[10px] text-white">{g.home}</b>}
                            </div>
                        </div>
                    );
                })}
            </div>
            {team(home)}
        </div>
    );
}
